import { howItWorksSteps } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

function ProcessStep({ step, delay, isLast }: { step: typeof howItWorksSteps[0]; delay: number; isLast: boolean }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Connector line */}
      {!isLast && (
        <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary-300 to-transparent" />
      )}

      {/* Step number + icon */}
      <div className="relative mb-5">
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/15">
          <i className={`${step.icon} text-2xl md:text-3xl text-background-50`} />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
          <span className="text-xs font-bold text-background-50">{step.number}</span>
        </div>
      </div>

      <h3 className="text-lg md:text-xl font-semibold text-foreground-900 mb-2">
        {step.title}
      </h3>
      <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xs">
        {step.description}
      </p>
    </div>
  );
}

export default function ProcessSection() {
  const [titleRef, titleInView] = useInView<HTMLDivElement>();

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-background-50">
      {/* Header */}
      <div
        ref={titleRef}
        className={`text-center max-w-2xl mx-auto mb-12 md:mb-16 transition-all duration-700 ${
          titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
          <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">Process</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-3">
          How it works
        </h2>
        <p className="text-base md:text-lg text-foreground-600">
          Four steps to biometric trust across every digital touchpoint.
        </p>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
        {howItWorksSteps.map((step, i) => (
          <ProcessStep
            key={step.id}
            step={step}
            delay={i * 120}
            isLast={i === howItWorksSteps.length - 1}
          />
        ))}
      </div>
    </section>
  );
}